import { useResources } from '@/api'
import { getProp } from '@/api/parsing'
import { useAppSelector } from '@/app'
import { CalendarProvider, useCalendarSelectors } from '@/features/calendar'
import { RouteList } from '@/features/calendar/components'
import { DateController } from '@/features/calendar/controllers'
import { ZoomController } from '@/features/calendar/controllers/ZoomController'
import { useAutoscroll } from '@/features/calendar/util'
import {
  selectors as coreSelectors,
  GlobalLoadingIndicator,
} from '@/features/core'
import { Resource } from '@alexandrainst/plana-react-api'
import { Box, Drawer, Grid, LinearProgress, Paper } from '@mui/material'
import { useState } from 'react'
import { PlanningMainMenu } from './PlanningMainMenu'
import { useStyles } from './PlanningMainView.style'
import { actions, selectors } from './planningSlice'

const PlanningMainViewContent = () => {
  const { classes } = useStyles()
  const {
    selectSelectedGroup,
    selectDate,
    selectScale,
    selectSelectedRoutes,
    selectGutterInterval,
    selectLegacyGrouping,
  } = useCalendarSelectors()
  const selectedGroup = useAppSelector(selectSelectedGroup)
  const date = useAppSelector(selectDate)
  const scale = useAppSelector(selectScale)
  const selectedRoutes = useAppSelector(selectSelectedRoutes)
  const gutterInterval = useAppSelector(selectGutterInterval)
  const legacyGrouping = useAppSelector(selectLegacyGrouping)
  const { isLoading, data: routes } = useResources('Route')
  const flags = useAppSelector(coreSelectors.selectFlags)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const propGroups = (r: Resource): string[] => getProp(r, 'groups') ?? []

  const routesInSelectedGroup = routes?.filter(
    r => propGroups(r) && propGroups(r).indexOf(selectedGroup) !== -1
  )

  const { ref: scrollContainerRef } = useAutoscroll(date, [], scale)

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      spacing={0}
      sx={{ background: '#1c78d3' }}
    >
      <Drawer
        anchor="top"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      >
        <Box>
          <PlanningMainMenu />
        </Box>
      </Drawer>
      <Grid size={{ xs: 8, md: 8 }}>
        <DateController />
      </Grid>
      <Grid size="grow">
        <Box
          pr={2}
          sx={{ textAlign: 'right' }}
          onClick={() => flags.useBurgerMenu && setIsDrawerOpen(!isDrawerOpen)}
        >
          <GlobalLoadingIndicator />
        </Box>
      </Grid>
      <Grid size={{ md: 12, xs: 12 }} sx={{ width: '100%' }}>
        <LinearProgress
          variant={isLoading ? 'indeterminate' : 'determinate'}
          value={100}
        />
        <Paper elevation={3} className={classes.container}>
          <ZoomController />
          <div ref={scrollContainerRef} className={classes.routeContainer}>
            <RouteList
              ids={
                [...(routesInSelectedGroup ?? [])]
                  .filter(
                    it =>
                      selectedRoutes.length === 0 ||
                      selectedRoutes.includes(it.data.id)
                  )
                  ?.sort((a, b) =>
                    (getProp(a.data.properties, 'name') ?? '').localeCompare(
                      getProp(b.data.properties, 'name') ?? ''
                    )
                  )
                  .map(r => r.data.id) ?? []
              }
              date={date}
              scale={scale}
              interval={gutterInterval}
              legacyLayout={legacyGrouping}
            />
          </div>
        </Paper>
      </Grid>
    </Grid>
  )
}

export const PlanningMainView = () => {
  return (
    <CalendarProvider
      calendarSelector={selectors.selectCalendar}
      calendarActions={actions}
    >
      <PlanningMainViewContent />
    </CalendarProvider>
  )
}
